import { Crumb } from "@/components/bread-crumbs/Crumb";
import { PageHeader } from "@/components/page-header/PageHeader";
import { PageLayout } from "@/components/page-layout/PageLayout";
import { UnAuthorizedView } from "@/components/unauthorized-view/UnAuthorizedView";
import { useRebateAgreementBookings } from "@/features/rebates/hooks/UseRebateAgreementBookings";
import { hasClaim } from "@/lib/claims/utils";
import { profileSelectors } from "@/redux/profile/selectors";
import { routeFactory } from "@/RouteFactory";
import { SpaceValues } from "@/lib/utils/SpaceValues";
import { OPEN_REBATE } from "@/types/claims/constants";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { RebateBookingsByRoute } from "@/features/rebates/components/bookings/RebateBookingsByRoute";
import { RebateBookingsCopyButton } from "@/features/rebates/components/bookings/RebateBookingsCopyButton";
import { RebateBookingsExportButton } from "@/features/rebates/components/bookings/RebateBookingsExportButton";
import { RebateBookingsSummary } from "@/features/rebates/components/bookings/RebateBookingsSummary";
import { Row, Space } from "@stenajs-webui/core";
import {
  BreadCrumbs,
  Card,
  CardBody,
  stenaTags,
} from "@stenajs-webui/elements";
import { ErrorScreen, LoadingScreen } from "@stenajs-webui/panels";
import { Helmet } from "react-helmet";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router";

export const HandleRebateDetails = () => {
  const { claims } = useSelector(profileSelectors.getGlobal);
  const { rebateAgreementNumber } = useParams() as {
    rebateAgreementNumber: string;
  };

  const [searchParams] = useSearchParams();

  const agreementNumber = Number(rebateAgreementNumber);

  /**
   * Handle rebates opens this page for one of its rows, which covers a single
   * goal grouping on the agreement. Without the goal ids we would fall back to
   * the agreement's first overview row and show the wrong period's bookings.
   */
  const goalIds = useMemo(
    () => searchParams.get("goalIds")?.split(",").filter(Boolean) ?? [],
    [searchParams]
  );

  const { loading, error, overview } = useRebateAgreementBookings(
    agreementNumber,
    goalIds
  );

  if (!hasClaim(claims, [OPEN_REBATE])) {
    return <UnAuthorizedView />;
  }

  return (
    <>
      <Helmet title={`Rebate ${rebateAgreementNumber} - FPP`} />

      <PageHeader
        title={overview?.rebateAgreement?.name ?? `Rebate ${agreementNumber}`}
        icon={stenaTags}
        breadcrumbs={
          <BreadCrumbs>
            <Crumb
              label={"Handle rebates"}
              path={routeFactory.rebates.handle()}
              dataTestid={testIdConstants.rebateBookingsBreadcrumbHandleRebates}
            />
            <Crumb label={String(agreementNumber)} />
          </BreadCrumbs>
        }
      />

      <PageLayout>
        {loading || error || !overview ? (
          <Card>
            <CardBody>
              {loading ? (
                <LoadingScreen />
              ) : error ? (
                <ErrorScreen text={error.message} />
              ) : (
                <ErrorScreen
                  text={
                    goalIds.length === 0
                      ? "No rebate goals selected. Open this page from Handle rebates."
                      : `Found no rebate goals on agreement number ${agreementNumber}.`
                  }
                />
              )}
            </CardBody>
          </Card>
        ) : (
          <>
            <Card>
              <CardBody>
                <Row alignItems={"center"} justifyContent={"space-between"}>
                  <RebateBookingsSummary rebate={overview} />
                  <Space num={SpaceValues.SIXTEEN} />
                  <Row alignItems={"center"}>
                    <RebateBookingsCopyButton rebate={overview} />
                    <Space num={SpaceValues.EIGHT} />
                    <RebateBookingsExportButton rebate={overview} />
                  </Row>
                </Row>
              </CardBody>
            </Card>
            <RebateBookingsByRoute rebate={overview} />
          </>
        )}
      </PageLayout>
    </>
  );
};
